import React, { useEffect, useState } from "react";
import api from "@/api";
import {
  TrendingUp, Plus, AlertCircle, CheckCircle, ChevronDown, ChevronUp,
  Building2, CreditCard, FileText, Loader2, Receipt, Calendar, User
} from "lucide-react";

function TopupModal({ companies, onClose, onSave, preselect }) {
  const [companyId, setCompanyId] = useState(preselect || "");
  const [form, setForm] = useState({
    page_credits: 0,
    ai_credits: 0,
    remarks: "",
    invoice_number: "",
    topup_date: new Date().toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async (e) => {
    e.preventDefault();
    if (!companyId) return setError("Please select a company");
    setSaving(true);
    setError("");
    try {
      await api.post(`/admin/companies/${companyId}/topup`, form);
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || "Top-up failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-lg shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#E5F0F2] flex items-center justify-center text-[#006080]">
            <TrendingUp size={14} />
          </div>
          <h3 className="font-bold text-slate-900">New Credit Top-Up</h3>
        </div>
        <form onSubmit={handleSave} className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle size={13} className="flex-shrink-0" /> {error}
            </div>
          )}

          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Company <span className="text-red-500">*</span></label>
            <select
              data-testid="topup-company-select"
              value={companyId}
              onChange={e => setCompanyId(e.target.value)}
              className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
            >
              <option value="">Select a company…</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Page Credits</label>
              <input
                data-testid="topup-page-credits"
                type="number" min={0}
                value={form.page_credits}
                onChange={e => setForm(f => ({ ...f, page_credits: parseInt(e.target.value) || 0 }))}
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">AI Credits</label>
              <input
                data-testid="topup-ai-credits"
                type="number" min={0}
                value={form.ai_credits}
                onChange={e => setForm(f => ({ ...f, ai_credits: parseInt(e.target.value) || 0 }))}
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Invoice / Credit Note #</label>
              <input
                data-testid="topup-invoice-number"
                type="text"
                value={form.invoice_number}
                onChange={e => setForm(f => ({ ...f, invoice_number: e.target.value }))}
                placeholder="INV-2024-001"
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Top-Up Date</label>
              <input
                data-testid="topup-date"
                type="date"
                value={form.topup_date}
                onChange={e => setForm(f => ({ ...f, topup_date: e.target.value }))}
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Remarks</label>
            <textarea
              data-testid="topup-remarks"
              value={form.remarks}
              onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
              rows={2}
              placeholder="e.g. Monthly plan renewal, promotional credits…"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] resize-none"
            />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 h-9 border border-slate-200 text-sm font-medium text-slate-700 rounded-md hover:bg-slate-50 transition-colors">Cancel</button>
            <button
              data-testid="topup-save-button"
              type="submit"
              disabled={saving}
              className="flex-1 h-9 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md disabled:opacity-50 transition-colors"
            >
              {saving ? "Saving…" : "Apply Top-Up"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LogTable({ logs }) {
  if (!logs.length) {
    return (
      <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
        <Receipt size={28} className="text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500">No top-up records yet</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50">
            {["Date", "Company", "Page Credits", "AI Credits", "Invoice #", "Remarks", "By"].map(h => (
              <th key={h} className="text-left text-xs font-bold uppercase tracking-wide text-slate-500 px-4 py-3">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {logs.map((log, i) => (
            <tr key={log.id} data-testid={`topup-log-row-${log.id}`} className={`border-b border-slate-100 hover:bg-slate-50 ${i % 2 === 1 ? "bg-slate-50/50" : ""}`}>
              <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{log.topup_date || log.created_at?.slice(0, 10)}</td>
              <td className="px-4 py-3 font-medium text-slate-900">{log.company_name || log.company_id?.slice(0, 8)}</td>
              <td className="px-4 py-3">
                {log.page_credits > 0 ? (
                  <span className="text-[#006080] font-semibold">+{log.page_credits.toLocaleString()}</span>
                ) : <span className="text-slate-400">—</span>}
              </td>
              <td className="px-4 py-3">
                {log.ai_credits > 0 ? (
                  <span className="text-[#006080] font-semibold">+{log.ai_credits.toLocaleString()}</span>
                ) : <span className="text-slate-400">—</span>}
              </td>
              <td className="px-4 py-3 font-mono text-xs text-slate-600">{log.invoice_number || "—"}</td>
              <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{log.remarks || "—"}</td>
              <td className="px-4 py-3 text-slate-500 text-xs">{log.created_by_name || "System"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CompanyCard({ company, onTopup }) {
  const pageRemain = company.page_credits - company.page_credits_used;
  const aiRemain = company.ai_credits - company.ai_credits_used;
  const pagePct = company.page_credits > 0 ? Math.min(100, (company.page_credits_used / company.page_credits) * 100) : 0;
  const aiPct = company.ai_credits > 0 ? Math.min(100, (company.ai_credits_used / company.ai_credits) * 100) : 0;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-[#E5F0F2] flex items-center justify-center text-[#006080] font-bold text-xs">
            {company.name[0].toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">{company.name}</p>
            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${company.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
              {company.is_active ? "Active" : "Archived"}
            </span>
          </div>
        </div>
        <button
          onClick={() => onTopup(company)}
          data-testid={`topup-company-btn-${company.id}`}
          className="flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium bg-[#006080] text-white rounded-md hover:bg-[#004B66] transition-colors"
        >
          <Plus size={11} /> Top Up
        </button>
      </div>

      <div className="space-y-2">
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] text-slate-500">Page Credits</span>
            <span className="text-[11px] font-medium text-slate-700">{pageRemain.toLocaleString()} / {company.page_credits.toLocaleString()}</span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${pagePct >= 90 ? "bg-red-500" : pagePct >= 75 ? "bg-amber-500" : "bg-[#006080]"}`} style={{ width: `${pagePct}%` }} />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] text-slate-500">AI Credits</span>
            <span className="text-[11px] font-medium text-slate-700">{aiRemain.toLocaleString()} / {company.ai_credits.toLocaleString()}</span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${aiPct >= 90 ? "bg-red-500" : aiPct >= 75 ? "bg-amber-500" : "bg-[#006080]"}`} style={{ width: `${aiPct}%` }} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CreditTopup() {
  const [companies, setCompanies] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [preselect, setPreselect] = useState(null);
  const [activeTab, setActiveTab] = useState("companies");
  const [filterCompany, setFilterCompany] = useState("");

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/companies"),
      api.get("/admin/topup-log"),
    ]).then(([c, l]) => {
      setCompanies(c.data);
      setLogs(l.data);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const handleTopup = (company = null) => {
    setPreselect(company?.id || null);
    setShowModal(true);
  };

  const filteredLogs = filterCompany
    ? logs.filter(l => l.company_id === filterCompany)
    : logs;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Credit Top-Up</h2>
          <p className="text-sm text-slate-500 mt-0.5">Manage company credit allocations and view top-up history.</p>
        </div>
        <button
          onClick={() => handleTopup()}
          data-testid="new-topup-button"
          className="flex items-center gap-2 h-9 px-4 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md transition-colors"
        >
          <Plus size={14} /> New Top-Up
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        {["companies", "history"].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors capitalize ${
              activeTab === tab
                ? "border-[#006080] text-[#006080]"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {tab === "companies" ? "Company Credits" : "Top-Up History"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#006080] border-t-transparent rounded-full animate-spin" /></div>
      ) : activeTab === "companies" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {companies.filter(c => c.is_active).map(c => (
            <CompanyCard key={c.id} company={c} onTopup={handleTopup} />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <select
              value={filterCompany}
              onChange={e => setFilterCompany(e.target.value)}
              className="h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
            >
              <option value="">All Companies</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <span className="text-xs text-slate-400">{filteredLogs.length} records</span>
          </div>
          <LogTable logs={filteredLogs} />
        </div>
      )}

      {showModal && (
        <TopupModal
          companies={companies.filter(c => c.is_active)}
          preselect={preselect}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); fetchAll(); }}
        />
      )}
    </div>
  );
}
